import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Printer , CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// Logo embedded as a data URI so print windows never race the image load
let __logoDataUrl = null;
async function getLogoDataUrl() {
  if (__logoDataUrl) return __logoDataUrl;
  try {
    const res = await fetch('/himan-logo.png');
    const blob = await res.blob();
    __logoDataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch {
    __logoDataUrl = `${window.location.origin}/himan-logo.png`;
  }
  return __logoDataUrl;
}

export default function Invoices({ user }) {
  const privs = user?.privileges || [];
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  // finance/admin hold the invoices privilege and validate proformas;
  // sales hold proforma_invoices and may only raise proformas
  const canValidate = isAdmin || privs.includes('*') || privs.includes('invoices');
  const proformaOnly = !canValidate;
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    order_id: '',
    customer_id: '',
    invoice_type: 'invoice',
    items: [{ product_name: '', quantity: 1, unit_price: 0, amount: 0 }],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0,
    payment_terms: '',
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchOrders();
    fetchCustomers();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [internalRes, customerRes] = await Promise.allSettled([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/admin/customer-orders`, { headers })
      ]);
      const internal = internalRes.status === 'fulfilled' ? internalRes.value.data : [];
      // Portal orders become invoiceable once approved; hide ones already invoiced
      const invoiceable = ['approved', 'shipped', 'delivered', 'completed'];
      const customerOrders = (customerRes.status === 'fulfilled' ? customerRes.value.data : [])
        .filter((o) => invoiceable.includes(o.status) && !o.invoice_id);
      setOrders([...internal, ...customerOrders]);
    } catch (error) {
      console.error('Failed to fetch orders');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_name: '', quantity: 1, unit_price: 0, amount: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    newItems[index].amount = newItems[index].quantity * newItems[index].unit_price;
    setFormData({ ...formData, items: newItems }, calculateTotals);
    calculateTotals(newItems);
  };

  const calculateTotals = (items = formData.items) => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = subtotal * 0.18;
    const totalAmount = subtotal + taxAmount;
    setFormData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total_amount: totalAmount
    }));
  };

  const handleOrderSelect = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      const items = order.items.map(item => ({
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        amount: item.quantity * item.unit_price
      }));
      setFormData({
        ...formData,
        order_id: orderId,
        customer_id: order.customer_id,
        items
      });
      calculateTotals(items);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice created successfully');
      setOpen(false);
      resetForm();
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      order_id: '',
      customer_id: '',
      invoice_type: proformaOnly ? 'proforma' : 'invoice',
      items: [{ product_name: '', quantity: 1, unit_price: 0, amount: 0 }],
      subtotal: 0,
      tax_amount: 0,
      total_amount: 0,
      payment_terms: '',
      notes: ''
    });
  };

  const getCustomerName = (invoiceOrId) => {
    // Accept either the invoice doc (preferred) or a bare id
    if (invoiceOrId && typeof invoiceOrId === 'object') {
      const inv = invoiceOrId;
      if (inv.customer_details?.name) return inv.customer_details.name;
      const c = customers.find((c) => c.id === inv.customer_id);
      return c ? c.name : 'Unknown';
    }
    const customer = customers.find((c) => c.id === invoiceOrId);
    return customer ? customer.name : 'Unknown';
  };

  const handlePrint = async (invoice) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    if (!printWindow) { toast.error('Popup blocked — allow popups to print'); return; }
    const logoSrc = await getLogoDataUrl();
    // Prefer embedded snapshot; fall back to live customer lookup
    const snapshot = invoice.customer_details || {};
    const live = customers.find(c => c.id === invoice.customer_id) || {};
    const customer = {
      name: snapshot.name || live.name || 'N/A',
      company_name: snapshot.company_name || live.company_name || '',
      phone: snapshot.phone || live.phone || '',
      email: snapshot.email || live.email || '',
      address: snapshot.address || live.address || '',
      delivery_point: snapshot.delivery_point || live.delivery_point || '',
      customer_type: snapshot.customer_type || live.customer_type || '',
    };
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${invoice.invoice_type === 'proforma' ? 'Proforma Invoice' : 'Invoice'} - ${invoice.invoice_number}</title>
        <style>
          body { font-family: 'Montserrat', Arial, sans-serif; padding: 40px; color: #010101; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #8FC043; padding-bottom: 20px; }
          .logo { max-width: 100px; margin: 0 auto 15px; }
          .company-name { color: #8FC043; font-weight: 700; font-size: 24px; margin: 10px 0; }
          .info { margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background-color: #8FC043; color: white; font-weight: 600; }
          .total { text-align: right; font-weight: bold; color: #010101; }
          .highlight { color: #EA5829; font-weight: 600; }
        </style>
      </head>
      <body>
        <div class="header">
          <img src="${logoSrc}" alt="Himan Logo" class="logo" />
          <div class="company-name">Himan Investment & Company</div>
          <h2 style="color: #EA5829;">${invoice.invoice_type === 'proforma' ? 'Proforma Invoice' : 'Invoice'}</h2>
          <p><strong>Invoice Number:</strong> ${invoice.invoice_number}</p>
          <p><strong>Date:</strong> ${new Date(invoice.created_at).toLocaleDateString()}</p>
        </div>
        <div class="info">
          <p><strong>Customer:</strong> ${customer.name || 'N/A'}</p>
          <p><strong>Company:</strong> ${customer.company_name || 'N/A'}</p>
          <p><strong>Phone:</strong> ${customer.phone || 'N/A'}</p>
          <p><strong>Email:</strong> ${customer.email || 'N/A'}</p>
          <p><strong>Address:</strong> ${customer.address || 'N/A'}</p>
          ${customer.delivery_point ? `<p><strong>Delivery Point:</strong> ${customer.delivery_point}</p>` : ''}
          ${customer.customer_type ? `<p><strong>Customer Type:</strong> ${String(customer.customer_type).toUpperCase()}</p>` : ''}
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items.map(item => {
              const qty = Number(item.quantity ?? 0);
              const unit = Number(item.unit_price ?? 0);
              const amt = Number(item.amount ?? item.total_price ?? qty * unit);
              return `
              <tr>
                <td>${item.product_name || ''}</td>
                <td>${qty}</td>
                <td>Tsh ${unit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>Tsh ${amt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
        <div class="total">
          <p>Subtotal: Tsh ${Number(invoice.subtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p>Tax (18%): Tsh ${Number(invoice.tax_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p><strong>Total: Tsh ${Number(invoice.total_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></p>
        </div>
        ${invoice.payment_terms ? `<p><strong>Payment Terms:</strong> ${invoice.payment_terms}</p>` : ''}
        ${invoice.notes ? `<p><strong>Notes:</strong> ${invoice.notes}</p>` : ''}
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    const doPrint = () => {
      if (printWindow.__printed) return;
      printWindow.__printed = true;
      printWindow.focus();
      printWindow.print();
    };
    printWindow.onload = doPrint;
    setTimeout(doPrint, 800);
  };

  const validateProforma = async (invoice) => {
    if (!window.confirm(`Validate ${invoice.invoice_number} and issue the official invoice?`)) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/invoices/${invoice.id}/validate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Validated — official invoice ${res.data.official_invoice.invoice_number} issued`);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Validation failed');
    }
  };

  const rejectProforma = async (invoice) => {
    const reason = window.prompt(`Reason for rejecting ${invoice.invoice_number}:`);
    if (reason === null) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices/${invoice.id}/reject-validation`, { reason }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Proforma rejected');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rejection failed');
    }
  };

  const validationBadge = (invoice) => {
    if (invoice.invoice_type !== 'proforma') {
      return invoice.validated_from_number
        ? <Badge className="bg-blue-50 text-blue-700" variant="outline">from {invoice.validated_from_number}</Badge>
        : null;
    }
    const map = {
      pending: ['bg-amber-100 text-amber-800', 'Pending validation'],
      validated: ['bg-green-100 text-green-800', `Validated → ${invoice.official_invoice_number || ''}`],
      rejected: ['bg-red-100 text-red-800', 'Rejected'],
    };
    const [cls, label] = map[invoice.validation_status] || map.pending;
    return <Badge className={cls} variant="outline" title={invoice.rejection_reason || ''}>{label}</Badge>;
  };

  return (
    <div data-testid="invoices-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Invoices</h1>
          <p className="text-slate-600 mt-1">Manage invoices and proforma invoices</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-button" onClick={resetForm}>
              <Plus className="mr-2" size={16} />
              Create Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
            <DialogHeader>
              <DialogTitle>Create New Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice_type">Invoice Type</Label>
                  <Select value={formData.invoice_type} disabled={proformaOnly}
                    onValueChange={(value) => setFormData({ ...formData, invoice_type: value })}>
                    <SelectTrigger data-testid="invoice-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {!proformaOnly && <SelectItem value="invoice">Invoice</SelectItem>}
                      <SelectItem value="proforma">Proforma Invoice</SelectItem>
                    </SelectContent>
                  </Select>
                  {proformaOnly && (
                    <p className="text-xs text-gray-400">You raise proforma invoices; the finance team validates them into official invoices.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="order">Select Order</Label>
                  <Select value={formData.order_id} onValueChange={handleOrderSelect}>
                    <SelectTrigger data-testid="order-select">
                      <SelectValue placeholder="Select order" />
                    </SelectTrigger>
                    <SelectContent>
                      {orders.map((order) => (
                        <SelectItem key={order.id} value={order.id}>
                          {order.order_number}{order.customer_name ? ` — ${order.customer_name}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Invoice Items</Label>
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-4 gap-2" data-testid={`invoice-item-${index}`}>
                    <Input
                      placeholder="Product"
                      value={item.product_name}
                      onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={item.amount.toFixed(2)}
                      disabled
                    />
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    data-testid="payment-terms-input"
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    placeholder="e.g., Net 30 days"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    data-testid="notes-input"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span data-testid="subtotal">Tsh {formData.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax (18%):</span>
                  <span data-testid="tax-amount">Tsh {formData.tax_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span data-testid="total-amount">Tsh {formData.total_amount.toFixed(2)}</span>
                </div>
              </div>

              <Button data-testid="save-invoice-button" type="submit" className="w-full">
                Create Invoice
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice Number</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500">
                  No invoices found. Create your first invoice.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                  <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {invoice.invoice_type === 'proforma' ? 'Proforma' : 'Invoice'}
                    </Badge>
                  </TableCell>
                  <TableCell>{getCustomerName(invoice)}</TableCell>
                  <TableCell>Tsh {invoice.total_amount.toFixed(2)}</TableCell>
                  <TableCell>{new Date(invoice.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{validationBadge(invoice)}</TableCell>
                  <TableCell className="text-right">
                    {canValidate && invoice.invoice_type === 'proforma' && (invoice.validation_status || 'pending') === 'pending' && (
                      <>
                        <Button data-testid={`validate-invoice-${invoice.id}`} variant="ghost" size="icon"
                          title="Validate — issue official invoice" onClick={() => validateProforma(invoice)}>
                          <CheckCircle2 size={16} className="text-green-600" />
                        </Button>
                        <Button data-testid={`reject-invoice-${invoice.id}`} variant="ghost" size="icon"
                          title="Reject proforma" onClick={() => rejectProforma(invoice)}>
                          <XCircle size={16} className="text-red-600" />
                        </Button>
                      </>
                    )}
                    <Button
                      data-testid={`print-invoice-${invoice.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => handlePrint(invoice)}
                    >
                      <Printer size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
